import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";

const CreatePackagePage = () => {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [totalTestsIncluded, setTotalTestsIncluded] = useState("");
  const [description, setDescription] = useState("");
  const [precautions, setPrecautions] = useState("");
  const [gender, setGender] = useState("");
  const [selectedDiagnostics, setSelectedDiagnostics] = useState([]);
  const [diagnosticsList, setDiagnosticsList] = useState([]);
  const [includedTests, setIncludedTests] = useState([
    { name: "", subTestCount: 0, subTests: [""] },
  ]);
  const [loading, setLoading] = useState(false);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [errors, setErrors] = useState({});

  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  // Fetch diagnostics list from API
  useEffect(() => {
    const fetchDiagnostics = async () => {
      try {
        setLoadingDiagnostics(true);
        const response = await axios.get("https://api.elthiumhealth.com/api/admin/alldiagnostics");
        
        if (response.data && response.data.data) {
          setDiagnosticsList(response.data.data);
        } else {
          setDiagnosticsList([]);
        }
      } catch (error) {
        console.error("Error fetching diagnostics:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load diagnostics list",
        });
        setDiagnosticsList([]);
      } finally {
        setLoadingDiagnostics(false);
      }
    };

    fetchDiagnostics();
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Validate form - REMOVED price > 0 validation
  const validateForm = () => {
    const newErrors = {};
    
    // Name validation
    if (!name.trim()) {
      newErrors.name = "Package name is required";
    }
    
    // Price validation - only check if field is empty, not if it's 0
    if (price === "" || price === null || price === undefined) {
      newErrors.price = "Price is required";
    } else if (isNaN(Number(price))) {
      newErrors.price = "Price must be a valid number";
    } else if (Number(price) < 0) {
      newErrors.price = "Price cannot be negative";
    }
    
    // Total tests validation - must be greater than 0
    if (!totalTestsIncluded || Number(totalTestsIncluded) <= 0) {
      newErrors.totalTestsIncluded = "Total tests must be greater than 0";
    }
    
    // Description validation
    if (!description.trim()) {
      newErrors.description = "Description is required";
    }
    
    // Precautions validation
    if (!precautions.trim()) {
      newErrors.precautions = "Instructions are required";
    }
    
    // Gender validation
    if (!gender) {
      newErrors.gender = "Gender selection is required";
    }
    
    // Diagnostics validation
    if (selectedDiagnostics.length === 0) {
      newErrors.diagnostics = "Please select at least one diagnostic";
    }
    
    // Included tests validation
    includedTests.forEach((test, index) => {
      if (!test.name.trim()) {
        newErrors[`testName_${index}`] = "Test name is required";
      }
      if (test.subTestCount <= 0) {
        newErrors[`testCount_${index}`] = "Sub test count must be greater than 0";
      }
      test.subTests.forEach((subTest, subIndex) => {
        if (!subTest.trim()) {
          newErrors[`subTest_${index}_${subIndex}`] = "Sub test name is required";
        }
      });
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle diagnostic checkbox change
  const handleDiagnosticChange = (diagnosticId) => {
    setSelectedDiagnostics(prev => {
      if (prev.includes(diagnosticId)) {
        return prev.filter(id => id !== diagnosticId);
      } else {
        return [...prev, diagnosticId];
      }
    });
    if (errors.diagnostics) {
      setErrors(prev => ({ ...prev, diagnostics: undefined }));
    }
  };

  // Select all diagnostics
  const handleSelectAll = () => {
    if (selectedDiagnostics.length === diagnosticsList.length) {
      setSelectedDiagnostics([]);
    } else {
      const allIds = diagnosticsList.map(diagnostic => diagnostic._id);
      setSelectedDiagnostics(allIds);
    }
    if (errors.diagnostics) {
      setErrors(prev => ({ ...prev, diagnostics: undefined }));
    }
  };

  // Remove selected diagnostic
  const handleRemoveDiagnostic = (diagnosticId) => {
    setSelectedDiagnostics(prev => prev.filter(id => id !== diagnosticId));
  };

  // Clear all selections
  const handleClearAll = () => {
    setSelectedDiagnostics([]);
  };

  const handleAddTest = () => {
    setIncludedTests([...includedTests, { name: "", subTestCount: 0, subTests: [""] }]);
  };

  const handleRemoveTest = (index) => {
    const updated = includedTests.filter((_, i) => i !== index);
    setIncludedTests(updated);
    
    // Remove related errors
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach(key => {
      if (key.startsWith(`testName_${index}`) || 
          key.startsWith(`testCount_${index}`) || 
          key.startsWith(`subTest_${index}`)) {
        delete newErrors[key];
      }
    });
    setErrors(newErrors);
  };

  const handleTestChange = (index, field, value) => {
    const updated = [...includedTests];
    updated[index][field] = field === "subTestCount" ? Number(value) : value;
    setIncludedTests(updated);
    
    // Clear related error
    const errorKey = field === "subTestCount" ? `testCount_${index}` : `testName_${index}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    }
  };

  const handleSubTestChange = (testIndex, subTestIndex, value) => {
    const updated = [...includedTests];
    updated[testIndex].subTests[subTestIndex] = value;
    setIncludedTests(updated);
    
    // Clear related error
    const errorKey = `subTest_${testIndex}_${subTestIndex}`;
    if (errors[errorKey]) {
      setErrors(prev => ({ ...prev, [errorKey]: undefined }));
    }
  };

  const handleAddSubTest = (testIndex) => {
    const updated = [...includedTests];
    updated[testIndex].subTests.push("");
    setIncludedTests(updated);
  };

  // Remove sub test
  const handleRemoveSubTest = (testIndex, subTestIndex) => {
    const updated = [...includedTests];
    updated[testIndex].subTests = updated[testIndex].subTests.filter((_, index) => index !== subTestIndex);
    setIncludedTests(updated);
    
    // Remove related error
    const errorKey = `subTest_${testIndex}_${subTestIndex}`;
    if (errors[errorKey]) {
      const newErrors = { ...errors };
      delete newErrors[errorKey];
      setErrors(newErrors);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      Swal.fire({
        icon: "warning",
        title: "Validation Error",
        text: "Please fix the errors in the form before submitting",
      });
      return;
    }

    const payload = {
      name,
      price: Number(price),
      totalTestsIncluded: Number(totalTestsIncluded),
      description,
      precautions,
      gender,
      includedTests,
      diagnosticIds: selectedDiagnostics,
    };

    try {
      setLoading(true);
      await axios.post("https://api.elthiumhealth.com/api/admin/create-packages", payload);

      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Package created successfully and linked to selected diagnostics!",
      });

      navigate("/package-list");
    } catch (error) {
      console.error("Error creating package:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error?.response?.data?.message || "Failed to create package.",
      });
    } finally {
      setLoading(false);
    }
  };

  // Get selected diagnostic names
  const getSelectedDiagnosticNames = () => {
    return selectedDiagnostics.map(id => {
      const diagnostic = diagnosticsList.find(d => d._id === id);
      return diagnostic ? diagnostic.name : "";
    });
  };

  // Price input change handler - ALLOW 0 value
  const handlePriceChange = (e) => {
    const value = e.target.value;
    // Allow 0 and positive numbers
    if (value === "" || (!isNaN(value) && Number(value) >= 0)) {
      setPrice(value);
      // Clear price error if present
      if (errors.price) {
        setErrors(prev => ({ ...prev, price: undefined }));
      }
    }
  };

  // Total tests input change handler with validation
  const handleTotalTestsChange = (e) => {
    const value = e.target.value;
    // Allow only positive numbers
    if (value === "" || (Number(value) >= 0 && !isNaN(value))) {
      setTotalTestsIncluded(value);
      // Clear total tests error if present
      if (errors.totalTestsIncluded) {
        setErrors(prev => ({ ...prev, totalTestsIncluded: undefined }));
      }
    }
  };

  return (
    <div className="p-6 bg-white rounded shadow-md">
      <h3 className="text-lg font-bold mb-4">Create a New Package</h3>
      <form onSubmit={handleSubmit}>
        {/* Row 1: Name & Price */}
        <div className="flex gap-4 mb-4">
          <div className="w-1/2">
            <label className="block text-sm mb-1">Package Name <span className="text-red-500">*</span></label>
            <input
              type="text"
              className={`p-2 border rounded w-full ${errors.name ? 'border-red-500' : ''}`}
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors(prev => ({ ...prev, name: undefined }));
                }
              }}
              required
            />
            {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
          </div>
          <div className="w-1/2">
            <label className="block text-sm mb-1">MRP (₹) <span className="text-red-500">*</span></label>
            <input
              type="number"
              min="0" 
              step="0.01"
              className={`p-2 border rounded w-full ${errors.price ? 'border-red-500' : ''}`}
              value={price}
              onChange={handlePriceChange}
              required
            />
            {errors.price && <p className="text-red-500 text-xs mt-1">{errors.price}</p>}
            <p className="text-xs text-gray-500 mt-1">Enter 0 for free package</p> {/* Changed helper text */}
          </div>
        </div>

        {/* Row 2: Total Tests Included & Gender */}
        <div className="flex gap-4 mb-4">
          <div className="w-1/2">
            <label className="block text-sm mb-1">Total Tests Included <span className="text-red-500">*</span></label>
            <input
              type="number"
              min="1"
              className={`p-2 border rounded w-full ${errors.totalTestsIncluded ? 'border-red-500' : ''}`}
              value={totalTestsIncluded}
              onChange={handleTotalTestsChange}
              required
            />
            {errors.totalTestsIncluded && <p className="text-red-500 text-xs mt-1">{errors.totalTestsIncluded}</p>}
            <p className="text-xs text-gray-500 mt-1">Must be greater than 0</p>
          </div>
          <div className="w-1/2">
            <label className="block text-sm mb-1">Gender <span className="text-red-500">*</span></label>
            <select
              className={`p-2 border rounded w-full ${errors.gender ? 'border-red-500' : ''}`}
              value={gender}
              onChange={(e) => {
                setGender(e.target.value);
                if (errors.gender) {
                  setErrors(prev => ({ ...prev, gender: undefined }));
                }
              }}
              required
            >
              <option value="">Select Gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Both">Both</option>
            </select>
            {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender}</p>}
          </div>
        </div>

        {/* Row 3: Description & Precautions */}
        <div className="flex gap-4 mb-4">
          <div className="w-1/2">
            <label className="block text-sm mb-1">Package Description <span className="text-red-500">*</span></label>
            <textarea
              className={`p-2 border rounded w-full h-[86px] ${errors.description ? 'border-red-500' : ''}`}
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                if (errors.description) {
                  setErrors(prev => ({ ...prev, description: undefined }));
                }
              }}
              required
            />
            {errors.description && <p className="text-red-500 text-xs mt-1">{errors.description}</p>}
          </div>
          <div className="w-1/2">
            <label className="block text-sm mb-1">Instructions's <span className="text-red-500">*</span></label>
            <textarea
              className={`p-2 border rounded w-full h-[86px] ${errors.precautions ? 'border-red-500' : ''}`}
              value={precautions}
              onChange={(e) => {
                setPrecautions(e.target.value);
                if (errors.precautions) {
                  setErrors(prev => ({ ...prev, precautions: undefined }));
                }
              }}
              required
            />
            {errors.precautions && <p className="text-red-500 text-xs mt-1">{errors.precautions}</p>}
          </div>
        </div>

        {/* Diagnostics Selection with Dropdown Checkboxes */}
        <div className="mb-4" ref={dropdownRef}>
          <label className="block text-sm font-medium mb-2">
            Select Diagnostics <span className="text-red-500">*</span> 
            {selectedDiagnostics.length > 0 && `(${selectedDiagnostics.length} selected)`}
          </label>

          {/* Selected Items Display */}
          {selectedDiagnostics.length > 0 && (
            <div className="mb-2 p-2 border rounded bg-gray-50">
              <div className="flex flex-wrap gap-2">
                {getSelectedDiagnosticNames().map((name, index) => (
                  <span
                    key={selectedDiagnostics[index]}
                    className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded"
                  >
                    {name}
                    <button
                      type="button"
                      onClick={() => handleRemoveDiagnostic(selectedDiagnostics[index])}
                      className="ml-1 text-blue-600 hover:text-blue-800"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex justify-between items-center mt-2">
                <span className="text-xs text-gray-500">
                  {selectedDiagnostics.length} diagnostic(s) selected
                </span>
                <button
                  type="button"
                  onClick={handleClearAll}
                  className="text-red-500 text-xs hover:underline"
                >
                  Clear All
                </button>
              </div>
            </div>
          )}

          {/* Dropdown */}
          <div className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className={`w-full p-2 border rounded bg-white text-left flex justify-between items-center ${errors.diagnostics ? 'border-red-500' : ''}`}
            >
              <span>
                {selectedDiagnostics.length === 0 
                  ? "Choose Diagnostics" 
                  : `${selectedDiagnostics.length} diagnostic(s) selected`
                }
              </span>
              <span className="text-gray-400">▼</span>
            </button>

            {errors.diagnostics && <p className="text-red-500 text-xs mt-1">{errors.diagnostics}</p>}

            {dropdownOpen && (
              <div className="absolute z-10 w-full mt-1 bg-white border rounded shadow-lg max-h-60 overflow-y-auto">
                {loadingDiagnostics ? (
                  <div className="p-3 text-center text-gray-500">
                    Loading diagnostics...
                  </div>
                ) : diagnosticsList.length > 0 ? (
                  <>
                    {/* Select All Option */}
                    <div className="p-2 border-b bg-gray-50">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={selectedDiagnostics.length === diagnosticsList.length}
                          onChange={handleSelectAll}
                          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <span className="ml-2 text-sm font-medium">
                          {selectedDiagnostics.length === diagnosticsList.length 
                            ? "Deselect All" 
                            : "Select All"
                          }
                        </span>
                      </label>
                    </div>

                    {/* Diagnostics List */}
                    <div className="p-2">
                      {diagnosticsList.map((diagnostic) => (
                        <label
                          key={diagnostic._id}
                          className="flex items-center p-2 hover:bg-gray-100 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={selectedDiagnostics.includes(diagnostic._id)}
                            onChange={() => handleDiagnosticChange(diagnostic._id)}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                          />
                          <span className="ml-2 text-sm text-gray-700">
                            {diagnostic.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </>
                ) : (
                  <div className="p-3 text-center text-gray-500">
                    No diagnostics available
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Included Tests Section */}
        <div className="mb-4">
          <h4 className="text-md font-semibold mb-2">Included Sub Tests</h4>
          {includedTests.map((test, i) => (
            <div key={i} className="mb-4 border p-4 rounded bg-gray-50">
              <div className="flex gap-4 mb-2">
                <div className="w-1/2">
                  <label className="block text-sm mb-1">Test Name <span className="text-red-500">*</span></label>
                  <input
                    type="text"
                    className={`p-2 border rounded w-full ${errors[`testName_${i}`] ? 'border-red-500' : ''}`}
                    value={test.name}
                    onChange={(e) => handleTestChange(i, "name", e.target.value)}
                    required
                  />
                  {errors[`testName_${i}`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`testName_${i}`]}</p>
                  )}
                </div>
                <div className="w-1/2">
                  <label className="block text-sm mb-1">Sub Test Count <span className="text-red-500">*</span></label>
                  <input
                    type="number"
                    min="1"
                    className={`p-2 border rounded w-full ${errors[`testCount_${i}`] ? 'border-red-500' : ''}`}
                    value={test.subTestCount}
                    onChange={(e) => handleTestChange(i, "subTestCount", e.target.value)}
                    required
                  />
                  {errors[`testCount_${i}`] && (
                    <p className="text-red-500 text-xs mt-1">{errors[`testCount_${i}`]}</p>
                  )}
                  <p className="text-xs text-gray-500 mt-1">Must be greater than 0</p>
                </div>
              </div>

              <div>
                <label className="block text-sm mb-1">Include Sub Tests <span className="text-red-500">*</span></label>
                {test.subTests.map((sub, j) => (
                  <div key={j} className="flex items-center gap-2 mb-2">
                    <input
                      type="text"
                      className={`p-2 border rounded w-full ${errors[`subTest_${i}_${j}`] ? 'border-red-500' : ''}`}
                      value={sub}
                      onChange={(e) => handleSubTestChange(i, j, e.target.value)}
                      required
                    />
                    {/* Remove Sub Test Button - Only show if there's more than one sub test */}
                    {test.subTests.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveSubTest(i, j)}
                        className="text-red-500 hover:text-red-700 p-1"
                        title="Remove this sub test"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleAddSubTest(i)}
                  className="text-blue-500 text-sm hover:underline mt-1"
                >
                  + Add Sub Test
                </button>
              </div>

              <div className="flex justify-end mt-2">
                <button
                  type="button"
                  onClick={() => handleRemoveTest(i)}
                  className="text-red-500 text-sm hover:underline"
                >
                  Remove Test
                </button>
              </div>
            </div>
          ))}

          <button
            type="button"
            onClick={handleAddTest}
            className="text-green-600 text-sm hover:underline"
          >
            + Add Another Test
          </button>
        </div>

        {/* Submit Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            className="px-4 py-2 text-white bg-blue-600 hover:bg-blue-700 rounded disabled:bg-blue-400"
            disabled={loading}
          >
            {loading ? "Creating..." : "Create Package"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreatePackagePage;